import { useQuery } from "@tanstack/react-query";
import { Card, Empty, Skeleton } from "antd";
import { useMemo } from "react";
import WarehouseFilter from "../components/Filters/WarehouseFilter";
import DetentionAlert from "../components/Homepage/DetentionAlert";
import DetentionReconciliation from "../components/Homepage/DetentionReconciliation";
import DocumentAlarm from "../components/Homepage/DocumentAlarm";
import GatewaySummary from "../components/Homepage/GatewaySummary";
import OriginSummary from "../components/Homepage/OriginSummary";
import ShipmentActivity from "../components/Homepage/ShipmentActivity";
import SupplierScoreboard from "../components/Homepage/SupplierScoreboard";
import { useGlobalFilters } from "../providers/GlobalFiltersProvider";
import HomepageService from "../services/Homepage/HomepageService";

export default function Homepage({ warehouses }) {
    const { filters } = useGlobalFilters();
    const homepageQuery = useQuery({
        queryKey: ["homepageData", filters],
        queryFn: ({ signal }) => HomepageService.getHomepageData({ ...filters }, signal),
        refetchInterval: 1 * 60 * 60 * 1000
    });

    const { data, isLoading, isError, isFetching } = homepageQuery;

    const visibility = useMemo(() => {
        if (!data) return {};
        return {
            gatewaySummary: data.gatewaySummary && Object.values(data.gatewaySummary).some(v => v > 0),
            documentAlarm: data.documentAlarm?.length > 0,
            detentionAlert: data.detentionAlert?.length > 0,
            detentionReconciliation: data.detentionReconciliation && (data.detentionReconciliation.current > 0 || data.detentionReconciliation.monthToDate > 0 || data.detentionReconciliation.financialYearToDate > 0),
            shipmentActivity: data.shipmentActivity && (data.shipmentActivity.monthToDate > 0 || data.shipmentActivity.lastMonth > 0 || data.shipmentActivity.lastQuarter > 0 || data.shipmentActivity.last12Months > 0),
            supplierScoreboard: data.supplierScoreboard && (data.supplierScoreboard?.monthly?.length > 0 || data.supplierScoreboard?.quarterly?.length > 0 || data.supplierScoreboard?.yearly?.length > 0),
            originSummary: data.originSummary?.some(o => o.count > 0),
        };
    }, [data]);

    const hasAnyVisibleCard = Object.values(visibility).some(Boolean);

    return (
        <div className="px-2 h-full">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-2 mb-2 mx-1 pl-2 gap-y-1 md:gap-y-0">
                <div className="flex flex-row items-center w-full gap-x-1">
                    <span className="font-inter !font-medium text-base md:text-[34px] !text-[#015373]">
                        Dashboard
                    </span>
                </div>
                <div>
                    <div className="flex items-center font-inter">
                        <WarehouseFilter warehouses={warehouses} />
                    </div>
                </div>
            </div>
            <div className="bg-gray-50 rounded-3xl p-4">
                {(isLoading || isFetching)
                    ? <Card className="shadow-2xl rounded-xl">
                        <Skeleton active paragraph={{ rows: 5 }} />
                    </Card>
                    : isError ? (
                        <Card>
                            <Empty description="Error Loading Data" />
                        </Card>
                    ) : !hasAnyVisibleCard ? (
                        <Empty description="No Data Available" />
                    ) : <div className="grid grid-cols-12 gap-3">
                        {visibility.gatewaySummary && (
                            <div className="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4 2xl:col-span-3">
                                <GatewaySummary data={data.gatewaySummary} />
                            </div>
                        )}
                        {visibility.documentAlarm && (
                            <div className="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4 2xl:col-span-3">
                                <DocumentAlarm data={data.documentAlarm} />
                            </div>
                        )}
                        {visibility.detentionAlert && (
                            <div className="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4 2xl:col-span-3">
                                <DetentionAlert data={data.detentionAlert} />
                            </div>
                        )}
                        {visibility.detentionReconciliation && (
                            <div className="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4 2xl:col-span-3">
                                <DetentionReconciliation data={data.detentionReconciliation} />
                            </div>
                        )}
                        {visibility.shipmentActivity && (
                            <div className="col-span-12 lg:col-span-12 xl:col-span-8 2xl:col-span-5">
                                <ShipmentActivity data={data.shipmentActivity} />
                            </div>
                        )}
                        {visibility.supplierScoreboard && (
                            <div className="col-span-12 md:col-span-8 lg:col-span-8 xl:col-span-8 2xl:col-span-4">
                                <SupplierScoreboard data={data.supplierScoreboard} />
                            </div>
                        )}
                        {visibility.originSummary && (
                            <div className="col-span-12 md:col-span-4 lg:col-span-4 xl:col-span-4 2xl:col-span-3">
                                <OriginSummary data={data.originSummary} />
                            </div>
                        )}
                    </div>
                }
            </div>
        </div>
    );
}